// src/modules/leads/pages/LeadAnalytics.jsx
// Lead + call analytics for admin / CEO / CBO / branch managers.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Table, Segmented, DatePicker, Select, Space, Typography, Button, Tag, message,
} from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchBranches } from '../../branches/api';
import { fetchLeadOverview, fetchLeadByGroup, fetchLeadTimeSeries, rebuildLeadSnapshots } from '../api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function LeadAnalytics() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [groupBy, setGroupBy] = useState('branch');
  const [interval, setIntervalUnit] = useState('day');

  const [overview, setOverview] = useState({});
  const [groupRows, setGroupRows] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchBranches({ limit: 1000 }).then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {}); }, []);

  const params = useCallback(() => {
    const p = {};
    if (branchId) p.branchId = branchId;
    if (range?.[0]) p.startDate = range[0].startOf('day').toISOString();
    if (range?.[1]) p.endDate = range[1].endOf('day').toISOString();
    return p;
  }, [branchId, range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, gr, ts] = await Promise.all([
        fetchLeadOverview(params()),
        fetchLeadByGroup({ ...params(), groupBy }),
        fetchLeadTimeSeries({ ...params(), interval }),
      ]);
      setOverview(ov.data.data || {});
      setGroupRows(gr.data.data || []);
      setSeries(ts.data.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load analytics'); }
    finally { setLoading(false); }
  }, [params, groupBy, interval]);

  useEffect(() => { load(); }, [load]);

  const [rebuilding, setRebuilding] = useState(false);
  const rebuild = async () => {
    setRebuilding(true);
    try {
      await rebuildLeadSnapshots({ backfillDays: 30 });
      message.success('Missed-call history rebuilt (last 30 days)');
      load();
    } catch (err) { message.error(err?.response?.data?.message || 'Rebuild failed'); }
    finally { setRebuilding(false); }
  };

  const groupColumns = [
    { title: groupBy === 'csr' ? 'Representative' : 'Branch', dataIndex: 'label', render: (v, r) => <span>{v}{r.code ? <Text type="secondary"> ({r.code})</Text> : ''}</span> },
    { title: 'Total', dataIndex: 'total', align: 'right' },
    { title: 'Assigned', dataIndex: 'assigned', align: 'right' },
    { title: 'Contacted', dataIndex: 'contacted', align: 'right' },
    { title: 'Interested', dataIndex: 'interested', align: 'right' },
    { title: 'Converted', dataIndex: 'converted', align: 'right', render: (v) => <Text strong style={{ color: '#389e0d' }}>{v}</Text> },
    { title: 'Overdue', dataIndex: 'overdue', align: 'right', render: (v) => v ? <Tag color="orange">{v}</Tag> : 0 },
    { title: 'Missed', dataIndex: 'missed', align: 'right', render: (v) => v ? <Tag color="red">{v}</Tag> : 0 },
    { title: 'Conv %', dataIndex: 'conversionRate', align: 'right', render: (v) => `${v}%` },
  ];

  const seriesColumns = [
    { title: interval === 'month' ? 'Month' : 'Day', dataIndex: 'period' },
    { title: 'Leads Added', dataIndex: 'leadsAdded', align: 'right' },
    { title: 'Calls', dataIndex: 'calls', align: 'right' },
    { title: 'Connected', dataIndex: 'connected', align: 'right' },
    { title: 'Interested', dataIndex: 'interested', align: 'right' },
    { title: 'Converted', dataIndex: 'converted', align: 'right' },
    { title: 'Missed', dataIndex: 'missed', align: 'right', render: (v) => v ? <Tag color="red">{v}</Tag> : 0 },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Lead Analytics</Title></Col>
        <Col>
          <Space wrap>
            <Select allowClear showSearch optionFilterProp="children" style={{ width: 180 }} placeholder="All branches" value={branchId} onChange={(v) => setBranchId(v || null)}>
              {branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}
            </Select>
            <RangePicker value={range} onChange={(v) => setRange(v)} />
            <Button onClick={rebuild} loading={rebuilding}>Rebuild History</Button>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Total Leads" value={overview.total || 0} /></Card></Col>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Due Today" value={overview.dueToday || 0} valueStyle={{ color: '#1677ff' }} /></Card></Col>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Overdue" value={overview.overdue || 0} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Missed" value={overview.missed || 0} prefix={<WarningOutlined />} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Calls Today" value={overview.callsToday || 0} /></Card></Col>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Interested" value={overview.interested || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Converted" value={overview.converted || 0} valueStyle={{ color: '#389e0d' }} /></Card></Col>
        <Col xs={12} sm={6} md={3}><Card size="small"><Statistic title="Conversion" value={overview.conversionRate || 0} suffix="%" /></Card></Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}
        title={<Space>Breakdown <Segmented size="small" value={groupBy} onChange={setGroupBy} options={[{ label: 'Branch-wise', value: 'branch' }, { label: 'Person-wise', value: 'csr' }]} /></Space>}>
        <Table rowKey={(r) => r.id || r.label} size="small" columns={groupColumns} dataSource={groupRows} loading={loading}
          pagination={false} scroll={{ x: 800 }} locale={{ emptyText: 'No data' }} />
      </Card>

      <Card size="small"
        title={<Space>Trend <Segmented size="small" value={interval} onChange={setIntervalUnit} options={[{ label: 'Day-wise', value: 'day' }, { label: 'Month-wise', value: 'month' }]} /></Space>}>
        <Table rowKey="period" size="small" columns={seriesColumns} dataSource={series} loading={loading}
          pagination={{ pageSize: 15 }} locale={{ emptyText: 'No activity in range' }} />
      </Card>
    </div>
  );
}
